import { Code, ConnectError } from '@connectrpc/connect';
import { ViolationSchema } from '@release/connect-gen/buf/validate_pb';

import type { DescMessage, MessageShape, Registry } from '@bufbuild/protobuf';

export type HTTPMethod = string;
export type ErrorCause = 'network' | 'not-found' | 'permission' | 'already-exists' | 'unknown';
export type ErrorCustomContext = {
  target?: string;
  action?: string;
  cause?: ErrorCause;
  meta?: object;
};
export type ErrorOptions = {
  customMessage?: string;
  customCode?: ErrorCode;
} & ErrorCustomContext;

const errorCodes = [
  'file-size-exceeds-limit',
  'cloud-sign-invalid-email',
  'cloud-sign-invalid-phone-number',
  'cloud-sign-duplicated-phone-number',
  'cloud-sign-duplicated-email',
  'cloud-sign-upload-failure',
  'cloud-sign-already-signed-document',
  'cloud-sign-document-is-not-sendable',
  'sdp-print-error',
  'sdp-cancel-print',
  'sdp-render-failure',
  'sdp-invalid-print-data',
  'sdp-invalid-preview-cache',
  'not-invited',
  'disabled-connection',
  'unknown',
] as const;

export type ErrorCode = (typeof errorCodes)[number];

export const isErrorCode = (arg: string): arg is ErrorCode => {
  return errorCodes.includes(arg as ErrorCode);
};

export const isKnownErrorCode = (code: ErrorCode) => {
  return code !== 'unknown';
};

export const toErrorCode = (arg: string): ErrorCode => {
  if (isErrorCode(arg)) {
    return arg;
  } else {
    return 'unknown';
  }
};

export const errorCodeMap: Record<ErrorCode, string> = {
  'file-size-exceeds-limit': 'ファイルサイズが制限を超えています',
  // クラウドサイン系
  'cloud-sign-invalid-email': '無効なメールアドレスが含まれています',
  'cloud-sign-invalid-phone-number': '無効な電話番号が含まれています',
  'cloud-sign-duplicated-email': '重複したメールアドレスが含まれています',
  'cloud-sign-duplicated-phone-number': '重複した電話番号が含まれています',
  'cloud-sign-upload-failure': '書類のアップロードに失敗しました。ファイルが署名済みでないかご確認ください',
  'cloud-sign-already-signed-document': 'すでに署名された文書です',
  'cloud-sign-document-is-not-sendable': '書類が送信可能な状態ではありません。書類の状態をご確認ください',
  'sdp-print-error': '印刷に失敗しました',
  'sdp-cancel-print': '印刷がキャンセルされました',
  'sdp-render-failure': '印刷プレビューの表示に失敗しました',
  'sdp-invalid-print-data': '印刷データが無効です',
  'sdp-invalid-preview-cache': '印刷データが無効です',
  'not-invited':
    'ご利用のアカウントは、まだ会員登録・アカウント連携が完了していません。\n招待されたメールアドレスで会員登録を行うか、事業者の管理者にお問い合わせください。',
  'disabled-connection':
    'ご利用のログイン方法は、現在この組織では無効化されています。\n事業者の管理者にお問い合わせください。',
  unknown: 'エラーが発生しました',
};

export class CustomError extends Error {
  protected customCode: ErrorCode = 'unknown'; // 日本で表示可能なエラーコード
  protected customMessage?: string; // 表示可能なメッセージ

  // エラーの詳細情報。上記のcustomMessage, customCodeがない場合は、これらをもとに汎用的なエラーメッセージを生成する
  protected customContext: ErrorCustomContext = {};

  // メッセージで初期化した場合は、customMessageにセットする
  constructor(e: unknown, options?: ErrorOptions) {
    super(e instanceof Error ? e.message : undefined);
    if (e instanceof CustomError) {
      this.assign(e);
    }
    const customMessage = typeof e === 'string' ? e : options?.customMessage;
    if (customMessage) {
      this.customMessage = customMessage;
    }
    if (options?.customCode && options.customCode !== 'unknown') {
      this.customCode = options.customCode;
    }
    this.withContext({
      target: options?.target,
      action: options?.action,
      cause: options?.cause,
      meta: options?.meta,
    });
  }
  isKnownErrorCode() {
    return isKnownErrorCode(this.customCode);
  }
  assign(e: CustomError) {
    this.customMessage = e.customMessage;
    this.customCode = e.customCode;
    this.withContext(e.customContext);
    return this;
  }
  withContext(context: ErrorCustomContext) {
    this.customContext = {
      target: this.customContext.target || context.target,
      action: this.customContext.action || context.action,
      cause: this.customContext.cause || context.cause,
      meta: this.customContext.meta || context.meta,
    };
    return this;
  }
  toObject() {
    return {
      customMessage: this.customMessage,
      customCode: this.customCode,
      ...this.customContext,
    };
  }
  description(fallbackMessage?: string): string {
    // 1. ハンドリングされたエラーコードがある場合はそれを表示
    if (isKnownErrorCode(this.customCode)) {
      return errorCodeMap[this.customCode];
    }
    // 2. customMessageがある場合はそれを表示
    if (this.customMessage) {
      return this.customMessage;
    }
    // 3. targetとactionが与えられる場合は、それをもとにエラーメッセージを生成
    if (this.customContext) {
      const { action, target, cause } = this.customContext;
      switch (cause) {
        case 'network': {
          const verb = action ? `${action}中に` : '';
          const subject = verb && target ? `${target}の` : '';
          return `${subject}${verb}ネットワークエラーが発生しました`;
        }
        case 'not-found': {
          if (target) {
            return `${target}が見つかりません`;
          }
          break;
        }
        case 'already-exists':
          if (target) {
            return `${target}はすでに存在します`;
          }
          break;
        case 'permission': {
          const verb = action ? `${action}する` : '';
          const subject = verb && target ? `${target}を` : '';
          return `${subject}${verb}権限がありません`;
        }
      }
      if (action) {
        return `${target ? `${target}の` : ''}${action}に失敗しました`;
      }
    }
    // 4. 情報がない場合は、汎用的なエラーメッセージを表示
    return fallbackMessage ?? errorCodeMap.unknown;
  }
}

export class HTTPCustomError extends CustomError {
  httpStatus: number;
  httpMethod?: HTTPMethod;

  constructor(
    req: Request | RequestInit,
    res: Response,
    option?: Omit<ErrorOptions, 'cause' | 'action'> & {
      e?: unknown;
    }
  ) {
    const error = new Error('HTTP Error');
    if (option?.e instanceof Error) {
      super(option.e, option);
    }
    super(error, option);
    this.httpStatus = res.status;
    this.httpMethod = req.method;
    this.customContext = {};
    switch (this.httpStatus) {
      case 400:
        this.customContext.cause = 'unknown';
        break;
      case 401:
      case 403:
        this.customContext.cause = 'permission';
        break;
      case 404:
        this.customContext.cause = 'not-found';
        break;
      default:
        this.customContext.cause = 'unknown';
    }
    switch (this.httpMethod) {
      case 'GET':
        this.customContext.action = '取得';
        break;
      case 'POST':
        this.customContext.action = '登録';
        break;
      case 'PUT':
        this.customContext.action = '更新';
        break;
      case 'DELETE':
        this.customContext.action = '削除';
        break;
      case 'PATCH':
        this.customContext.action = '更新';
        break;
    }
  }
}

// ダブル継承ができないので、connectErrorは同じように振る舞うように実装している
export class ConnectCustomError extends CustomError implements ConnectError {
  connectError: ConnectError;

  get isWireError(): boolean {
    return this.connectError.isWireError;
  }

  readonly code: Code;
  readonly metadata: Headers;
  details: ConnectError['details'];
  readonly rawMessage: string;
  cause: unknown;
  name: string;

  setCustomCode(code: Code) {
    switch (code) {
      case Code.Unauthenticated:
      case Code.PermissionDenied:
        this.customContext.cause = 'permission';
        break;
      case Code.NotFound:
        this.customContext.cause = 'not-found';
        break;
      case Code.AlreadyExists:
        this.customContext.cause = 'already-exists';
        break;
      case Code.Aborted:
      case Code.Canceled:
      case Code.DataLoss:
      case Code.DeadlineExceeded:
        this.customContext.cause = 'network';
        break;
      default:
        this.customContext.cause = 'unknown';
    }
  }
  constructor(e: ConnectError, option?: Omit<ErrorOptions, 'e' | 'cause' | 'action'>) {
    super(e, option);
    this.connectError = e;
    this.code = e.code;
    this.metadata = e.metadata;
    this.details = e.details;
    this.rawMessage = e.rawMessage;
    this.cause = e.cause;
    this.name = e.name;
    this.setCustomCode(e.code);

    const details = this.connectError.findDetails(ViolationSchema);
    if (details.length > 0) {
      // バックエンドから詳細エラーメッセージが返ってきた場合はそれを表示する
      this.customMessage = details[0].message;
      // バックエンドからエラーコードが返ってきた場合はそれを表示する
      if (details[0].ruleId) {
        const code = toErrorCode(details[0].ruleId);
        if (isKnownErrorCode(code)) {
          this.customCode = code;
        }
      }
    }
  }
  findDetails<Desc extends DescMessage>(desc: Desc): MessageShape<Desc>[];
  findDetails(registry: Registry) {
    return this.connectError.findDetails(registry);
  }
}

/** バックエンドが返した項目ごとの検証エラー。field は proto のフィールド名（例: guest_last_name）。 */
export type FieldViolation = { field: string; message: string };

/**
 * バックエンドの検証エラーを、項目ごとに取り出す。
 *
 * 入力値の規則はバックエンドが持つ（例: backend/internal/fireins/guest_field_rules.go）。
 * 画面は判定を持たず、ここで取り出した field を自分のフォームの項目名へ読み替えて
 * react-hook-form の setError に渡す。
 *
 * field が載っていない violation は、どの欄に出せばよいか決められないので落とす。
 * それらは customMessage 経由でトーストに出る。
 */
export const extractFieldViolations = (e: unknown): FieldViolation[] => {
  if (!(e instanceof ConnectCustomError)) {
    return [];
  }
  return e
    .findDetails(ViolationSchema)
    .map((violation) => ({
      field: violation.field?.elements?.[0]?.fieldName ?? '',
      message: violation.message,
    }))
    .filter((violation): violation is FieldViolation => violation.field !== '' && violation.message !== '');
};
